package org.gstechnovos.jexonplastech.sections.home.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionProperty
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import kotlinx.coroutines.delay
import org.gstechnovos.jexonplastech.theme.unextractable
import org.gstechnovos.jexonplastech.util.LocalBreakpoint
import org.gstechnovos.jexonplastech.util.Res
import org.jetbrains.compose.web.css.*
import kotlin.time.Duration.Companion.milliseconds

/**
 *Images:Dimensions/Aspect Ratio: 1920x1080px (16:9 Widescreen).
 * File Size Constraint: Keep under 300KB (max 500KB); WebP format is highly preferred.
 * Rendering Behavior: The ambient background image covers the screen (ObjectFit.Cover),
 * while the active carousel images render at 50% width on desktop and 75% width on mobile.
 *
 * Text (If uncommented):Headline: Renders at 40px on desktop and 24px on mobile.
 * Keep to 3–6 words to prevent it from covering too much of the carousel images.
 * Button Text: Keep under 4 words (e.g., "Explore Our Products").
 */
// ==========================================
// 1. DATA MODEL
// ==========================================
data class HomeHeroSectionData(
    val sectionId: String? = null,
    val headline: String,
    val primaryButtonText: String,
    val primaryButtonPath: String,
    val sliderImages: List<String>
)

val defaultHeroSectionData = HomeHeroSectionData(
    sectionId = "home-header",
    headline = "Premium Homeware Excellence",
    primaryButtonText = "Explore Our Products",
    primaryButtonPath = "/products",
    sliderImages = listOf(
        Res.Image.Home.Hero.HOME_BANNER_1,
        Res.Image.Home.Hero.HOME_BANNER,
        Res.Image.Home.Hero.HOME_BANNER_2,
        Res.Image.Home.Hero.HOME_BANNER_3
    )
)

// ==========================================
// 2. COMPONENT
// ==========================================
@Composable
fun HomeHeroSection(data: HomeHeroSectionData = defaultHeroSectionData) {
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "home-header"
    val breakpoint = LocalBreakpoint.current
    val isMobile = breakpoint < Res.Breakpoints.MD

    val imageCount = data.sliderImages.size
    var activeIndex by remember { mutableStateOf(0) }

    // Auto-advance the slider every 4.5 seconds[cite: 15]
    LaunchedEffect(Unit) {
        if (imageCount > 1) {
            while (true) {
                delay(3000.milliseconds)
                activeIndex = (activeIndex + 1) % imageCount
            }
        }
    }

    Box(
        modifier = Modifier.id(targetId).fillMaxWidth().height(95.vh) // Keeps it fullscreen[cite: 15]
            .position(Position.Relative).overflow(Overflow.Hidden),
        // Removed the plain backgroundColor here to let the dynamic background show through
        contentAlignment = Alignment.Center
    ) {

        // --- 1. DYNAMIC BACKGROUND WITH GREY OVERLAY ---
        if (data.sliderImages.isNotEmpty()) {
            // A. The active image blurred in the background
            Image(
                src = data.sliderImages[activeIndex],
                alt = "Background ambient",
                modifier = Modifier.fillMaxSize().position(Position.Absolute).objectFit(ObjectFit.Cover).zIndex(1)
                    .styleModifier {
                        property("filter", "blur(20px)")
                        property("transition", "all 1s ease-in-out")
                    }.unextractable())

            // B. The Semi-Transparent Grey Overlay
            Box(
                modifier = Modifier.fillMaxSize().position(Position.Absolute)
                    // Adjust the alpha (0.75f) to make the background darker or lighter
                    .backgroundColor(rgba(40, 40, 40, 0.75f)).zIndex(2)
            )
        }

        // --- 2. THE 3D CAROUSEL SLIDER ---
        Box(
            modifier = Modifier.fillMaxWidth().height(if (isMobile) 50.vh else 65.vh).position(Position.Relative)
                .zIndex(3), // Placed above the background and overlay
            contentAlignment = Alignment.Center
        ) {
            data.sliderImages.forEachIndexed { index, imagePath ->
                val isActive = index == activeIndex
                val isNext = index == (activeIndex + 1) % imageCount
                val isPrev = index == (activeIndex - 1 + imageCount) % imageCount

                // Calculate 3D positioning[cite: 15]
                val xOffset = if (isActive) 0.percent else if (isNext) 25.percent else (-25).percent
                val scale = if (isActive) 1f else 0.75f
                val opacity = if (isActive) 1f else 0.1f
                val zIndex = if (isActive) 3 else 1

                Image(
                    src = imagePath,
                    alt = "Product Showcase $index",
                    modifier = Modifier.fillMaxHeight().width(if (isMobile) 75.percent else 50.percent)
                        .position(Position.Absolute).borderRadius(16.px)
                        .boxShadow(0.px, 20.px, 40.px, color = rgba(0, 0, 0, 0.5f)).objectFit(ObjectFit.Cover)
                        .zIndex(zIndex).opacity(opacity).scale(scale).translateX(xOffset)
                        // Smoothly animates the images sliding into place[cite: 15]
                        .transition(
                            Transition.of(
                                TransitionProperty.All,
                                duration = 800.ms,
                                timingFunction = AnimationTimingFunction.EaseInOut
                            )
                        )
                        // Allow users to click side images to manually navigate[cite: 15]
                        .onClick { if (!isActive) activeIndex = index }.styleModifier {
                            if (!isActive) property("cursor", "pointer")
                        }.unextractable())
            }
        }

        // --- 3. MINIMALIST TEXT & CTA OVERLAY ---
//        Column(
//            modifier = Modifier
//                .fillMaxWidth()
//                .align(Alignment.BottomCenter)
//                .padding(bottom = if (isMobile) 40.px else 60.px)
//                .zIndex(4), // Highest zIndex so it sits on top of everything
//            horizontalAlignment = Alignment.CenterHorizontally,
//            verticalArrangement = Arrangement.Center
//        ) {
//            SpanText(
//                text = data.headline,
//                modifier = Modifier
//                    .color(rgb(255, 255, 255))
//                    .fontSize(if (isMobile) 24.px else 40.px)
//                    .fontWeight(FontWeight.Bold)
//                    .textShadow(0.px, 4.px, 10.px, rgba(0, 0, 0, 0.8f))
//                    .margin(bottom = 24.px)
//                    .textAlign(TextAlign.Center)
//            )
//
//            SharedHeroActionLink(
//                text = data.primaryButtonText,
//                path = data.primaryButtonPath,
//                styleModifier = PrimaryButtonStyle.toModifier()
//            )
//        }
    }
}